import axios, { AxiosResponse } from 'axios'

import { RequestQuery } from '@models/apiBase'
import { Response } from '@models/apiBase'
import { WithFilter } from '@models/apiBase'
import { ID } from '@models/base'
import { MedicalRecordTypeEnum } from '@models/medicalRecord'
import { PatientVisitModel, PatientVisitPreviewDataPrintLabelModel, PatientVisitTypeEnum } from '@models/patientVisit'
import { PatientVisitSummaryModel } from '@models/patientVisitSummary'
import { PrinterDataModel } from '@models/printerData'

import { API_URL } from '@/constants/constant'
import { MedicalRecordPatientVisitModel } from '@/models/medicalRecordPatientVisit'

const PATIENT_VISIT_URL = `${API_URL}/patient-visits`

type ResponsePatientVisits = Response<WithFilter<Array<PatientVisitModel>>>
type ResponsePatientVisit = Response<{
  patient_visit: PatientVisitModel
}>
type ResponseMedicalRecordPatientVisits = Response<WithFilter<Array<MedicalRecordPatientVisitModel>>>
type ResponsePatientVisitSummaries = Response<WithFilter<Array<PatientVisitSummaryModel>>>

type ResponsePrintLabel = Response<{
  printer_data: PrinterDataModel
  signature: string
}>

type ResponsePreviewDataPrintLabel = Response<PatientVisitPreviewDataPrintLabelModel>

type PayloadFilterPatientVisitSummary = Pick<RequestQuery, 'limit' | 'page'> & {
  medical_record_type: MedicalRecordTypeEnum | null
  patient_id: ID
}

type PayloadUpdatePatientVisit = {
  note?: string
  patient_id: ID
  patient_relative_id: ID
  type: PatientVisitTypeEnum
}

type PayloadPatientVisitPrintLabel = {
  clinic_printer_id: ID
  qty: number
}

type PayloadFilterPatientVisits = RequestQuery<{
  patient_id: ID | null
  types: Array<PatientVisitTypeEnum> | null
}>

type PayloadPatientVisitGetOptionsForMedicalRecord = RequestQuery

interface IPatientVisitsAPI {
  filter: (query: PayloadFilterPatientVisits) => Promise<ResponsePatientVisits>

  get: (id: ID) => Promise<ResponsePatientVisit>

  update: (id: ID, payload: PayloadUpdatePatientVisit) => Promise<ResponsePatientVisit>

  getOptionsForMedicalRecord: (
    query: PayloadPatientVisitGetOptionsForMedicalRecord
  ) => Promise<ResponseMedicalRecordPatientVisits>

  getSummaries: (query: PayloadFilterPatientVisitSummary) => Promise<ResponsePatientVisitSummaries>

  getInsuranceCardFileForMedicalRecord: (patientVisitId: ID) => Promise<Blob>

  getInsuranceCardFileForQueueNumber: (patientVisitId: ID) => Promise<Blob>

  printLabel: (patientVisitId: ID, payload: PayloadPatientVisitPrintLabel) => Promise<ResponsePrintLabel>

  getPreviewDataPrintLabel: (patientVisitId: ID) => Promise<ResponsePreviewDataPrintLabel>
}

export const PatientVisitsAPI: IPatientVisitsAPI = {
  filter: async (query) => {
    return axios.post(`${PATIENT_VISIT_URL}/filter`, query).then((r: AxiosResponse<ResponsePatientVisits>) => r.data)
  },

  get: async (id) => {
    return axios.get(`${PATIENT_VISIT_URL}/${id}`).then((r: AxiosResponse<ResponsePatientVisit>) => r.data)
  },

  update: async (id, payload) => {
    return axios.put(`${PATIENT_VISIT_URL}/${id}`, payload).then((r: AxiosResponse<ResponsePatientVisit>) => r.data)
  },

  getOptionsForMedicalRecord: async (query) => {
    return axios
      .post(`${PATIENT_VISIT_URL}/options/medical-record`, query)
      .then((r: AxiosResponse<ResponseMedicalRecordPatientVisits>) => r.data)
  },

  getSummaries: async (query) => {
    return axios
      .post(`${PATIENT_VISIT_URL}/summarize-histories`, query)
      .then((r: AxiosResponse<ResponsePatientVisitSummaries>) => r.data)
  },

  getInsuranceCardFileForMedicalRecord: async (patientVisitId) => {
    return axios
      .get(`${PATIENT_VISIT_URL}/${patientVisitId}/insurance-card-file/medical-record`, {
        responseType: 'blob',
      })
      .then((r: AxiosResponse) => r.data)
  },

  getInsuranceCardFileForQueueNumber: async (patientVisitId) => {
    return axios
      .get(`${PATIENT_VISIT_URL}/${patientVisitId}/insurance-card-file/queue-number`, {
        responseType: 'blob',
      })
      .then((r: AxiosResponse) => r.data)
  },

  printLabel: async (id, payload) => {
    return axios.post(`${PATIENT_VISIT_URL}/${id}/print-label`, payload).then((r: AxiosResponse) => r.data)
  },

  getPreviewDataPrintLabel: async (id) => {
    return axios.post(`${PATIENT_VISIT_URL}/${id}/print-label/preview`).then((r: AxiosResponse) => r.data)
  },
}

export type {
  PayloadFilterPatientVisitSummary,
  PayloadUpdatePatientVisit,
  PayloadPatientVisitPrintLabel,
  PayloadFilterPatientVisits,
  PayloadPatientVisitGetOptionsForMedicalRecord,
}
